import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { type core, z } from 'zod';

/**
 * Cache buster for the locale files, which keep unhashed URLs under public/.
 * Injected by vite.config.ts; absent under Vitest, which skips that config.
 */
const I18N_VERSION =
  typeof __I18N_VERSION__ === 'string' ? __I18N_VERSION__ : 'dev';

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'fr'],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    cleanCode: true,

    ns: [
      'translation',
      'login',
      'profile',
      'home',
      'asset-allocation',
      'assetIdentityVault',
      'validation',
      'sms',
      'adminCenter',
      'enterprise',
      'esimQr',
      'api-key-management',
      'identities',
    ],

    defaultNS: 'translation',

    backend: {
      loadPath: '/i18n/locales/{{lng}}/{{ns}}.json',
      queryStringParams: { v: I18N_VERSION },
    },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },
  });

// A namespace that fails to load renders raw keys, which reads as a missing translation.
i18n.on('failedLoading', (lng, ns, msg) => {
  console.error(`[i18n] failed to load namespace "${ns}" for "${lng}": ${msg}`);
});

// Zod error map remains same
const customErrorMap: core.$ZodErrorMap<core.$ZodIssue> = (issue) => {
  if (issue.message && issue.message.includes('.')) {
    // Wait for i18n if not ready yet
    if (!i18n.isInitialized) {
      return { message: issue.message };
    }

    const translated = i18n.t(issue.message, {
      ns: 'enterprise',
    });

    // Check if translation was found (i18n returns the key if not found)
    if (translated !== issue.message) {
      return { message: translated };
    }
  }
  // Return null to use default Zod error
  return null;
};

z.setErrorMap(customErrorMap);

export default i18n;
